<template>
  <div>
    <div v-if="Object.keys(myOrders).length === 0">
      <h1 class="section-title" :style="{ color: sectionTitleColorTheme }">
        {{ $t("cart.noOrders") }}
      </h1>
    </div>

    <div v-if="Object.keys(myOrders).length > 0">
      <h1 class="section-title" :style="{ color: sectionTitleColorTheme }">
        {{ $t("cart.yourLastOrders") }}
      </h1>
      <div class="recent-orders" v-for="(order, key) in myOrders" :key="key">
        <font-awesome-icon
          v-if="isDone(order)"
          icon="check-circle"
          class="done-icon"
        />
        <div ref="content" class="recent-order-content">
          <div class="recent-orders-inside-wrapper">
            <div class="recent-orders-details">
              <span class="date">{{ formatDate(getDate(order)) }}</span>
              <span class="time">
                {{ formatTime(getDate(order)) }} &nbsp;
                <span class="recent-orders-qr">{{
                  tableQRLabel(order[0].tableQR)
                }}</span>
              </span>
            </div>

            <div
              class="product-type-section"
              v-if="getMenuGroupOrder(order).length > 0"
            >
              <ul
                class="recent-orders-items-list"
                v-for="(menugroup, index) in getMenuGroupOrder(order)"
                :key="`menugroup_${index}`"
              >
                <li class="recent-orders-item">
                  <span class="quantity">
                    {{ menugroup.quantity }}
                    <em>x</em>
                  </span>
                  <span class="name text-uppercase">{{
                    translate("order", menugroup)
                  }}</span>
                  <div v-if="menugroup.notes" class="notes">
                    <span>{{ menugroup.notes }}</span>
                  </div>
                  <span class="price">{{
                    formatPrice(menugroup.price * menugroup.quantity)
                  }}</span>
                </li>
                <li
                  class="order-metadata-title text-uppercase"
                  v-if="menugroup.starters.length > 0"
                >
                  {{ $t("common.first") }}
                </li>
                <li
                  class="recent-orders-item"
                  v-for="food in menugroup.starters"
                  :key="`starter_${food.id}`"
                >
                  <span class="name">{{ translate("order", food) }}</span>
                  <span class="price">{{
                    formatPrice(food.additional_charge * menugroup.quantity)
                  }}</span>
                </li>
                <li
                  class="order-metadata-title text-uppercase"
                  v-if="menugroup.maincourse.length > 0"
                >
                  {{ $t("common.mainCourses") }}
                </li>
                <li
                  class="recent-orders-item"
                  v-for="food in menugroup.maincourse"
                  :key="`maincourse_${food.id}`"
                >
                  <span class="name">{{ translate("order", food) }}</span>
                  <span class="price">{{
                    formatPrice(food.additional_charge * menugroup.quantity)
                  }}</span>
                </li>
                <li
                  class="order-metadata-title text-uppercase"
                  v-if="menugroup.desserts.length > 0"
                >
                  {{ $t("common.dessert") }}
                </li>
                <li
                  class="recent-orders-item"
                  v-for="dessert in menugroup.desserts"
                  :key="`dessert_${dessert.id}`"
                >
                  <span class="name">{{ translate("order", dessert) }}</span>
                  <span class="price">{{
                    formatPrice(dessert.additional_charge * menugroup.quantity)
                  }}</span>
                </li>
                <li
                  class="order-metadata-title"
                  v-if="menugroup.drinks.length > 0"
                >
                  {{ $t("common.drinks") }}
                </li>
                <li
                  class="recent-orders-item"
                  v-for="drink in menugroup.drinks"
                  :key="`drink_${drink.id}`"
                >
                  <span class="name">{{ translate("order", drink) }}</span>
                  <span class="price">{{
                    formatPrice(drink.additional_charge * menugroup.quantity)
                  }}</span>
                </li>
              </ul>
            </div>
            <div class="product-type-section">
              <div v-if="getCartDrinks(order).length > 0">
                <h3 class="recent-orders-product-type">
                  {{ $t("common.drinks") }}
                </h3>
                <ul class="recent-orders-items-list">
                  <li
                    class="recent-orders-item"
                    v-for="(drink, index) in getCartDrinks(order)"
                    :key="`drink_${index}`"
                  >
                    <span class="quantity">
                      {{ drink.quantity }}
                      <em>x</em>
                    </span>
                    <span class="name">{{ translate("order", drink) }}</span>
                    <span class="size">{{ sizeText(drink) }}</span>
                    <div v-if="drink.notes" class="notes">
                      <span>{{ drink.notes }}</span>
                    </div>
                    <span class="price">
                      {{ formatPrice(drink.price * drink.quantity) }}
                    </span>
                  </li>
                </ul>
              </div>
              <div v-if="getCartFood(order).length > 0">
                <h3 class="recent-orders-product-type">
                  {{ $t("common.food") }}
                </h3>
                <ul class="recent-orders-items-list">
                  <li
                    class="recent-orders-item"
                    v-for="(food, index) in getCartFood(order)"
                    :key="`food_${index}`"
                  >
                    <span class="quantity">
                      {{ food.quantity }}
                      <em>x</em>
                    </span>
                    <span class="name">{{ translate("order", food) }}</span>
                    <div v-if="food.notes" class="notes">
                      <span>{{ food.notes }}</span>
                    </div>
                    <span class="price">
                      {{
                        weight(food) + formatPrice(food.price * food.quantity)
                      }}
                    </span>
                  </li>
                </ul>
              </div>
              <div class="total-price">
                <span class="label">{{ $t("payment.total") }}</span>
                <span class="total-price-value">
                  {{ formatPrice(getTotalPrice(order)) }}
                </span>
              </div>
              <div
                class="expandable-row"
                v-if="myComments[key] && myComments[key].length > 0"
              >
                <div class="table-cell">
                  <div class="general-notes">{{ $t("common.comments") }}</div>
                  <div class="general-desc">
                    {{ myComments[key][0].comment }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="text-center">
      <v-container>
        <v-row justify="center">
          <v-col cols="8">
            <v-container class="max-width">
              <v-pagination
                v-model="page"
                class="my-4"
                :length="pages"
                @input="onPageChange"
              ></v-pagination>
            </v-container>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <Loader
      :dialog-loader="loadingLoader"
      :message="$t('common.charging')"
    ></Loader>
  </div>
</template>

<script>
import Loader from "../../common/Loader";
import { ordersCollection } from "@/collections/orders";
import { orderCommentsCollection } from "@/collections/orderComments";

// eslint-disable-next-line no-unused-vars
const NO_QR = "NO QR";

export default {
  name: "recentPage",
  components: { Loader },
  data() {
    return {
      ordersCollection: [],
      orderComments: [],
      totalRecords: [],
      total: 0,
      pages: 0,
      perPage: 10,
      page: 1,
      loadingLoader: true
    };
  },
  created() {
    this.getOrderCollection();
  },
  firestore() {
    return {
      orderComments: orderCommentsCollection.where("email", "==", this.getEmail)
    };
  },
  computed: {
    myOrders() {
      return this.arrayGroupBy(this.ordersCollection, "time");
    },
    myComments() {
      return this.arrayGroupBy(this.orderComments, "time");
    },
    tableQR() {
      return this.$store.state.tableQR;
    },
    getEmail() {
      return this.$store.getters.getEmail(false);
    }
  },
  mounted() {
    let self = this;
    self.$store.commit("setMyOrderQuantity", 0);
    localStorage.setItem("myordersQuantity", 0);
  },
  methods: {
    weight(order) {
      return order.weight ? order.weight + "Kg \xa0\xa0" : " ";
    },
    getOrderCollection() {
      try {
        ordersCollection
          .where("email", "==", this.getEmail)
          .orderBy("time", "desc")
          .get()
          .then(res => {
            this.totalRecords = res.docs;
            this.total = res.size;
            this.pages = Math.ceil(this.total / this.perPage);
          });

        ordersCollection
          .where("email", "==", this.getEmail)
          .orderBy("time", "desc")
          .limit(this.perPage)
          .get()
          .then(query => {
            query.forEach(item => {
              this.ordersCollection.push(item.data());
            });
            this.loadingLoader = false;
          });
      } catch (error) {
        this.setlogError(
          "get_ordersCollection",
          "Obtained error message in getOrderCollection()",
          error.stack,
          this.$options.name
        );
        this.loadingLoader = false;
      }
    },
    onPageChange() {
      try {
        this.loadingLoader = true;
        ordersCollection
          .where("email", "==", this.getEmail)
          .orderBy("time", "desc")
          .startAt(this.totalRecords[this.perPage * (this.page - 1)])
          .limit(this.perPage)
          .get()
          .then(query => {
            this.ordersCollection = [];
            query.forEach(item => {
              this.ordersCollection.push(item.data());
            });
            this.loadingLoader = false;
          });
      } catch (error) {
        this.setlogError(
          "get_ordersCollection",
          "Obtained error message in onPageChange()",
          error.stack,
          this.$options.name
        );
        this.loadingLoader = false;
      }
    },
    isDone(orders) {
      let isDone = true;

      // eslint-disable-next-line no-unused-vars
      orders.forEach((item, index) => {
        if (item.isPending) {
          isDone = false;
        }
      });

      return isDone;
    },
    // eslint-disable-next-line no-unused-vars
    getDate(order, key) {
      return order.length > 0 ? order[0].time : undefined;
    },
    getCartDrinks(order) {
      const { drinks } = this.$store.state.productTypeIds;
      return order.filter(item => item.product_type_id === drinks);
    },
    getCartFood(order) {
      const { food } = this.$store.state.productTypeIds;
      return order.filter(item => item.product_type_id === food);
    },
    getMenuGroupOrder(order) {
      return order.filter(item => {
        if (item.isMenuGroupProduct) {
          return item;
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.section-title {
  text-transform: uppercase;
  font-size: 14px;
  text-align: center;
  letter-spacing: 3px;
  padding-top: 20px;
}
</style>
